import { useId } from 'react'
import { cx } from './cx.js'

// ── identity ──────────────────────────────────────────────────────────────
/**
 * A user icon's ring (docs/USER-ICONS.md): its color is presence (IC-01: green online, bone recent,
 * chrome quiet), its line the tier (IC-02: solid Trusted, dashed Pending, dotted with the inside dimmed
 * Anonymous). The badges in its corners are the app's (people/badges.tsx).
 */
export type Ring = 'online' | 'recent' | 'stale'
export type RingTier = 'trusted' | 'pending' | 'anonymous'
export function Silhouette({ size = 40 }: { size?: number }) {
  return (
    <svg viewBox="0 0 44 44" width={size} height={size} aria-hidden="true">
      <circle cx="22" cy="17" r="8" fill="#9aa0a6" />
      <path d="M6 44c2-11 9-15 16-15s14 4 16 15z" fill="#9aa0a6" />
    </svg>
  )
}
/**
 * You (bugs.md): the brand mark itself (Monogram's rings, ticks and glow, exactly) with the user
 * figure where the FM letters sit. The user icon in the header, answering the logo across from it;
 * no status on it (your status is on your map marker). (On the map you're your photo: pins.ts.)
 */
export function RadarYou({ size = 44 }: { size?: number }) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true">
      <defs>
        <filter id={`y${id}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3.5" result="b1" />
          <feGaussianBlur stdDeviation="7" result="b2" />
          <feMerge>
            <feMergeNode in="b2" />
            <feMergeNode in="b1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <g filter={`url(#y${id})`} stroke="#FF8400">
        <circle cx="256" cy="256" r="220" fill="none" strokeWidth="4.5" />
        <circle cx="256" cy="256" r="170" fill="none" strokeWidth="2.5" opacity="0.8" />
        <path d="M256 18v82M256 412v82M18 256h82M412 256h82" strokeWidth="5" strokeLinecap="square" />
      </g>
      {/* The Silhouette's figure (44-unit box), centered in the inner ring. */}
      <g fill="#E6E4DF" transform="translate(161 141.5) scale(4.32)">
        <circle cx="22" cy="17" r="8" />
        <path d="M6 44c2-11 9-15 16-15s14 4 16 15z" />
      </g>
    </svg>
  )
}
export function Avatar({
  size = 44,
  ring = 'stale',
  tier = 'trusted',
  src,
  alt = '',
  blur,
}: {
  size?: number
  ring?: Ring
  tier?: RingTier
  src?: string | null
  alt?: string
  /** the photo blurred until asked (the console's, where it isn't their SFW one) */
  blur?: boolean | undefined
}) {
  return (
    <span
      className={cx(
        'fm-avatar',
        `fm-avatar--${ring}`,
        tier !== 'trusted' && `fm-avatar--${tier}`,
        blur && src && 'fm-avatar--blur',
      )}
      style={{ width: size, height: size }}
    >
      {src ? (
        <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Silhouette size={Math.round(size * 0.9)} />
      )}
    </span>
  )
}
/**
 * The radar logo (apps/web/public/logo.svg): drawn inline so it stays sharp at any size and needs no
 * request. Bare (transparent) wherever it signs the screen; `tile` adds the charcoal square with
 * rounded corners where it stands for the app icon (install, a notification).
 */
export function Monogram({ size = 42, tile }: { size?: number; tile?: boolean }) {
  // Several logos can share a page, so the glow and chamfer ids are made unique (and url()-safe).
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  return (
    <svg
      className={cx('fm-monogram', tile && 'fm-monogram--tile')}
      width={size}
      height={size}
      viewBox="0 0 512 512"
      role="img"
      aria-label="Feral Mode"
    >
      <defs>
        <filter id={`g${id}`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="3.5" result="b1" />
          <feGaussianBlur stdDeviation="7" result="b2" />
          <feMerge>
            <feMergeNode in="b2" />
            <feMergeNode in="b1" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <mask id={`m${id}`}>
          <rect width="512" height="512" fill="#fff" />
          <polygon points="125,212 139.5,212 125,226.5" fill="#000" />
          <polygon points="237,212 251.5,212 237,226.5" fill="#000" />
        </mask>
      </defs>
      {tile && <rect width="512" height="512" fill="#141414" />}
      <g filter={`url(#g${id})`} stroke="#FF8400">
        <circle cx="256" cy="256" r="220" fill="none" strokeWidth="4.5" />
        <circle cx="256" cy="256" r="170" fill="none" strokeWidth="2.5" opacity="0.8" />
        <path d="M256 18v82M256 412v82M18 256h82M412 256h82" strokeWidth="5" strokeLinecap="square" />
      </g>
      <g mask={`url(#m${id})`} fill="#E6E4DF">
        <path
          transform="translate(125 300) scale(0.125 -0.125)"
          d="M75 706V0H293V243H812V404H293V544H841V706Z"
        />
        <path
          transform="translate(237 300) scale(0.125 -0.125)"
          d="M73 706V0H280V537L588 0H761L1070 534V0H1280V706H935L678 243L422 706Z"
        />
      </g>
    </svg>
  )
}
